/**
 * Registration Settings Tab - configure step order and behaviour.
 *
 * Lives under Settings > Registration.  Clara Vale design tokens.
 */

import { useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { AlertTriangle, ChevronUp, ChevronDown, GripVertical, Loader2, Settings2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  useRegistrationSettings,
  useUpdateRegistrationSettings,
  ALL_SEGMENTS,
  SEGMENT_LABELS,
  CERTIFICATE_SETTING_DEFAULTS,
  type SegmentTemplate,
  type CertificateSettings,
} from '@/admin/hooks/use-registration-settings'
import { useProductTemplates } from '@/admin/hooks/use-products'
import { SegmentFieldConfigDialog } from './segment-field-config-dialog'
import { ValidationRulesSection } from './validation-rules-section'
import { ApprovalRulesSection } from './approval-rules-section'
import { RegistrationFieldsSection } from './registration-fields-section'
import { useFeatureFlag, FLAGS } from '@/admin/hooks/use-feature-flag'
import api from '@/admin/lib/api'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'

// Certificate section toggles. Add a row here to expose a new boolean
// certificate setting - the draft state, save payload, and Switch rendering
// pick it up generically. `key` must match a key in `CertificateSettings`.
const CERTIFICATE_TOGGLES: {
  key: keyof CertificateSettings
  label: string
  description: string
}[] = [
  {
    key: 'enable_download',
    label: 'Enable download',
    description:
      'Let customers view and download their warranty certificate from their product page. Turn off if you do not issue certificates to customers.',
  },
]

function validateSettings(
  order: string[],
  enableExistingHost: boolean,
): string | null {
  const indexOf = (segment: string) => order.indexOf(segment)
  const productIdx = indexOf('product')

  if (order.length === 0) {
    return 'At least one segment is required.'
  }
  if (!order.includes('review')) {
    return 'The "Review" segment is required.'
  }
  if (order[order.length - 1] !== 'review') {
    return 'The "Review" segment must be the last step.'
  }
  for (const segment of ['host', 'warranty']) {
    const segmentIdx = indexOf(segment)
    // Only enforce ordering against the dedicated `product` step; the
    // combined `host_product` segment selects its own product, so it
    // doesn't need a preceding product step.
    if (segmentIdx >= 0 && (productIdx < 0 || productIdx > segmentIdx)) {
      const label = SEGMENT_LABELS[segment] || segment
      return `"${label}" must come after the "Product" step. Product selection only happens on the Product step.`
    }
  }
  if (enableExistingHost) {
    // Accept either "customer" or "purchase_customer" as the customer step.
    const customerIdx = Math.min(
      order.indexOf('customer') >= 0 ? order.indexOf('customer') : Infinity,
      order.indexOf('purchase_customer') >= 0 ? order.indexOf('purchase_customer') : Infinity,
    )
    // Both `host` and the combined `host_product` segments collect host
    // details, so either qualifies as the earliest host segment.
    const hostIdx = order.indexOf('host')
    const hostProductIdx = order.indexOf('host_product')
    const earliestHost = Math.min(
      hostIdx >= 0 ? hostIdx : Infinity,
      hostProductIdx >= 0 ? hostProductIdx : Infinity,
    )
    if (customerIdx === Infinity || (earliestHost < Infinity && customerIdx > earliestHost)) {
      return 'When "Existing host selection" is enabled, "Customer" must appear before any host segment.'
    }
  }
  return null
}

/** Which segments have configurable fields. */
const CONFIGURABLE_SEGMENTS = new Set([
  'customer',
  'purchase',
])

const SEGMENT_TEMPLATE_KIND_LABELS: Record<SegmentTemplate['kind'], string> = {
  product: 'Product',
  host: 'Host',
  transaction: 'Transaction',
  customer: 'Customer',
}

function TemplateChip({
  template,
  segment,
  onConfigure,
}: {
  template: SegmentTemplate
  segment: string
  onConfigure: (segment: string) => void
}) {
  const fieldsLabel = `${template.attribute_count} ${template.attribute_count === 1 ? 'field' : 'fields'}`
  const chipClass =
    'inline-flex items-center gap-1.5 rounded-[3px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-2 py-[3px] transition-colors hover:border-[var(--ink-3)] hover:bg-[var(--bg-subtle)]'

  const content = (
    <>
      <span className="font-mono text-[9px] tracking-[0.06em] text-[var(--ink-3)] uppercase">
        {SEGMENT_TEMPLATE_KIND_LABELS[template.kind] ?? template.kind}
      </span>
      <span className="text-[11px] text-[var(--ink)]">{template.name}</span>
      <span className="text-[11px] text-[var(--ink-3)]">{fieldsLabel}</span>
    </>
  )

  if (template.edit_url) {
    return (
      <Link to={template.edit_url} className={chipClass} title={`Edit ${template.name}`}>
        {content}
      </Link>
    )
  }

  // No edit URL - the customer pseudo-template points at the existing
  // "Configure Fields" data-capture dialog. Reuse the row's existing
  // onConfigure callback so the chip stays consistent with the button.
  if (template.configured_via === 'data_capture_config') {
    return (
      <Button
        variant="ghost"
        type="button"
        onClick={() => onConfigure(segment)}
        className={`${chipClass} h-auto rounded-[3px] border-[0.5px] shadow-none`}
        title="Configure data-capture fields"
      >
        {content}
      </Button>
    )
  }

  return <span className={chipClass}>{content}</span>
}

function SortableSegmentRow({
  segment,
  idx,
  isLast,
  templates,
  onConfigure,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  segment: string
  idx: number
  isLast: boolean
  templates: SegmentTemplate[] | undefined
  onConfigure: (segment: string) => void
  onMoveUp: (idx: number) => void
  onMoveDown: (idx: number) => void
  onRemove: (segment: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: segment,
    })

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={`flex flex-col px-3 py-2.5 ${
        idx % 2 === 0 ? 'bg-[var(--bg)]' : 'bg-[var(--bg-subtle)]'
      } ${!isLast ? 'border-b-[0.5px] border-b-[var(--rule)]' : ''}`}
      data-dragging={isDragging || undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon-xs"
            type="button"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            className="cursor-grab touch-none text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)] active:cursor-grabbing"
          >
            <GripVertical size={14} className="shrink-0" />
          </Button>
          <span className="text-[13px] text-[var(--ink)]">
            {SEGMENT_LABELS[segment] || segment}
          </span>
          <span className="rounded-[3px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-1.5 py-[1px] font-mono text-[10px] text-[var(--ink-3)]">
            {segment}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {CONFIGURABLE_SEGMENTS.has(segment) && (
            <Button
              variant="ghost"
              size="xs"
              type="button"
              onClick={() => onConfigure(segment)}
              className="mr-1 flex cursor-pointer items-center gap-1 text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Settings2 size={12} />
              Configure Fields
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            type="button"
            onClick={() => onMoveUp(idx)}
            disabled={idx === 0}
            className="text-[var(--ink-3)] disabled:opacity-20"
            title="Move up"
          >
            <ChevronUp size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            type="button"
            onClick={() => onMoveDown(idx)}
            disabled={isLast}
            className="text-[var(--ink-3)] disabled:opacity-20"
            title="Move down"
          >
            <ChevronDown size={14} />
          </Button>
          {segment !== 'review' && (
            <Button
              variant="ghost"
              size="icon-xs"
              type="button"
              onClick={() => onRemove(segment)}
              className="ml-1 text-[11px] text-[var(--ink-3)]"
              title="Remove segment"
            >
              &times;
            </Button>
          )}
        </div>
      </div>

      {templates && templates.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-9">
          {templates.map((template, i) => (
            <TemplateChip
              key={`${segment}-${template.kind}-${template.uuid ?? i}`}
              template={template}
              segment={segment}
              onConfigure={onConfigure}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegistrationSettingsTab() {
  const { data: settings, isLoading } = useRegistrationSettings()
  const updateMutation = useUpdateRegistrationSettings()
  const registrationFieldsEnabled = useFeatureFlag(FLAGS.REGISTRATION_FIELDS)

  // Configure fields dialog state
  const [configSegment, setConfigSegment] = useState<string | null>(null)
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null)
  const [draftEnableExistingHost, setDraftEnableExistingHost] = useState<boolean | null>(null)
  const [draftDetailView, setDraftDetailView] = useState<'property' | 'vehicle' | null>(null)
  const [draftCertificate, setDraftCertificate] = useState<Partial<CertificateSettings> | null>(null)
  const [dirty, setDirty] = useState(false)

  const order = useMemo(
    () => draftOrder ?? settings?.segment_order ?? [],
    [draftOrder, settings?.segment_order],
  )
  const enableExistingHost = useMemo(
    () =>
      draftEnableExistingHost ?? settings?.enable_existing_host_selection ?? false,
    [draftEnableExistingHost, settings?.enable_existing_host_selection],
  )
  const detailView = useMemo(
    () => draftDetailView ?? settings?.registration_detail_view ?? 'property',
    [draftDetailView, settings?.registration_detail_view],
  )
  const certificateSettings = useMemo<CertificateSettings>(
    () => ({
      ...CERTIFICATE_SETTING_DEFAULTS,
      ...(settings?.certificate_settings ?? {}),
      ...(draftCertificate ?? {}),
    }),
    [settings?.certificate_settings, draftCertificate],
  )
  const validationError = useMemo(
    () => validateSettings(order, enableExistingHost),
    [order, enableExistingHost],
  )
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const availableToAdd = useMemo(
    () => ALL_SEGMENTS.filter((s) => !order.includes(s)),
    [order],
  )

  const moveUp = useCallback(
    (idx: number) => {
      if (idx <= 0) return
      const next = [...order]
      ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
      setDraftOrder(next)
      setDirty(true)
    },
    [order],
  )

  const moveDown = useCallback(
    (idx: number) => {
      if (idx >= order.length - 1) return
      const next = [...order]
      ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
      setDraftOrder(next)
      setDirty(true)
    },
    [order],
  )

  const removeSegment = useCallback(
    (segment: string) => {
      setDraftOrder(order.filter((s) => s !== segment))
      setDirty(true)
    },
    [order],
  )

  const addSegment = useCallback(
    (segment: string) => {
      const next =
        order.length > 0 && order[order.length - 1] === 'review'
          ? [...order.slice(0, -1), segment, 'review']
          : [...order, segment]
      setDraftOrder(next)
      setDirty(true)
    },
    [order],
  )

  const toggleExistingHost = useCallback(() => {
    setDraftEnableExistingHost(!enableExistingHost)
    setDirty(true)
  }, [enableExistingHost])

  const toggleCertificate = useCallback(
    (key: keyof CertificateSettings) => {
      setDraftCertificate((prev) => ({
        ...(prev ?? {}),
        [key]: !certificateSettings[key],
      }))
      setDirty(true)
    },
    [certificateSettings],
  )

  const updateDetailView = useCallback((value: string) => {
    if (value !== 'property' && value !== 'vehicle') return
    setDraftDetailView(value)
    setDirty(true)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = order.findIndex((segment) => segment === active.id)
      const newIndex = order.findIndex((segment) => segment === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      setDraftOrder(arrayMove(order, oldIndex, newIndex))
      setDirty(true)
    },
    [order],
  )

  const handleSave = useCallback(async () => {
    if (validationError) {
      toast.error(validationError)
      return
    }
    try {
      await updateMutation.mutateAsync({
        segment_order: order,
        enable_existing_host_selection: enableExistingHost,
        registration_detail_view: detailView,
        certificate_settings: certificateSettings,
      })
      toast.success('Registration settings saved')
      setDraftOrder(null)
      setDraftEnableExistingHost(null)
      setDraftDetailView(null)
      setDraftCertificate(null)
      setDirty(false)
    } catch (err) {
      toastApiError(err, 'Failed to save registration settings');
    }
  }, [
    order,
    enableExistingHost,
    detailView,
    certificateSettings,
    validationError,
    updateMutation,
  ])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[var(--ink-3)]" size={20} />
      </div>
    )
  }

  return (
    <div className="max-w-[640px]">
      {/* Section: Segment Order */}
      <div className="mb-8">
        <p className="clara-section-label mb-4">
          Segment Order
        </p>
        <p className="clara-small mb-4">
          Drag or use arrows to reorder the registration wizard steps.
          Each segment maps to a step in the registration flow.
        </p>
        {validationError && (
          <div className="mb-4 rounded-[6px] border border-[rgba(220,38,38,0.2)] bg-[rgba(220,38,38,0.06)] px-4 py-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-error)]" />
              <div>
                <p className="m-0 text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--status-error)]">
                  Invalid step order
                </p>
                <p className="mt-1 text-[13px] text-[rgb(127,29,29)]">
                  {validationError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Segment list */}
        {order.length === 0 ? (
          <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
            <div className="px-5 py-5 text-center text-[13px] text-[var(--ink-3)]">
              No segments configured. Add segments below.
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={order}
              strategy={verticalListSortingStrategy}
            >
              <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
                {order.map((segment, idx) => (
                  <SortableSegmentRow
                    key={segment}
                    segment={segment}
                    idx={idx}
                    isLast={idx === order.length - 1}
                    templates={settings?.segment_metadata?.[segment]?.templates}
                    onConfigure={setConfigSegment}
                    onMoveUp={moveUp}
                    onMoveDown={moveDown}
                    onRemove={removeSegment}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Add segment */}
        {availableToAdd.length > 0 && (
          <div className="mt-3">
            <p className="clara-label mb-2">
              Add Segment
            </p>
            <div className="flex flex-wrap gap-2">
              {availableToAdd.map((segment) => (
                <Button
                  key={segment}
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => addSegment(segment)}
                  className="rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-1 text-[11px] text-[var(--ink-2)] transition-all duration-150 hover:border-[var(--green)] hover:text-[var(--green)]"
                >
                  + {SEGMENT_LABELS[segment] || segment}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Section: Preview strip */}
      {order.length > 0 && (
        <div className="mb-8">
          <p className="clara-section-label mb-3">
            Step Flow Preview
          </p>
          <div className="flex flex-wrap items-center gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
            {order.map((segment, idx) => (
              <div key={segment} className="flex items-center">
                <div className="flex items-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-1.5">
                  <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--green)] text-[9px] font-[400] text-white">
                    {idx + 1}
                  </span>
                  <span className="whitespace-nowrap text-[11px] text-[var(--ink)]">
                    {SEGMENT_LABELS[segment] || segment}
                  </span>
                </div>
                {idx < order.length - 1 && (
                  <div className="h-[0.5px] w-4 shrink-0 bg-[var(--rule-strong)]" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Section: Options */}
      <div className="mb-8">
        <p className="clara-section-label mb-4">
          Options
        </p>

        {/* Toggle: enable_existing_host_selection */}
        <Button
          variant="ghost"
          type="button"
          data-testid="registration-settings-host-toggle"
          onClick={toggleExistingHost}
          className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 text-left transition-[border-color] duration-150 hover:border-[var(--green-border)]"
        >
          <Switch checked={enableExistingHost} decorative />
          <div>
            <span className="block text-[13px] text-[var(--ink)]">
              Enable existing host selection
            </span>
            <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
              Allow selecting a previously registered host (vehicle, property, etc.)
              during registration. Requires the "Customer" step before any host step.
            </span>
          </div>
        </Button>

        <div className="mt-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3">
          <div className="mb-3">
            <span className="block text-[13px] text-[var(--ink)]">
              Registration detail view
            </span>
            <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
              Choose the view version used for non-property, non-draft
              registration records.
            </span>
          </div>
          <Select value={detailView} onValueChange={updateDetailView}>
            <SelectTrigger className="h-9 w-full rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[12px]">
              <SelectValue placeholder="Select detail view" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vehicle">Vehicle view</SelectItem>
              <SelectItem value="property">Property view</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Section: Certificate */}
      <div className="mb-8">
        <p className="clara-section-label mb-4">
          Certificate
        </p>
        <p className="clara-small mb-4">
          Control the warranty certificate options available to your customers.
        </p>
        <div className="flex flex-col gap-3">
          {CERTIFICATE_TOGGLES.map((toggle) => (
            <Button
              key={toggle.key}
              variant="ghost"
              type="button"
              data-testid={`registration-settings-certificate-${toggle.key}-toggle`}
              onClick={() => toggleCertificate(toggle.key)}
              className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 text-left transition-[border-color] duration-150 hover:border-[var(--green-border)]"
            >
              <Switch checked={certificateSettings[toggle.key]} decorative />
              <div>
                <span className="block text-[13px] text-[var(--ink)]">
                  {toggle.label}
                </span>
                <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
                  {toggle.description}
                </span>
              </div>
            </Button>
          ))}
        </div>
      </div>
      {/* Section: Early Registration Attributes (per template) */}
      {order.includes('host_context') && (
        <EarlyAttributesSection />
      )}
      {/* Section: Registration Fields (tenant-scoped, flag-gated) */}
      {registrationFieldsEnabled && (
        <div className="mb-8">
          <RegistrationFieldsSection />
        </div>
      )}
      {/* Section: Validation Rules (S3) */}
      <ValidationRulesSection />
      {/* Section: Approval Rules (S5) */}
      <ApprovalRulesSection />
      {/* Save button */}
      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          data-testid="registration-settings-save-btn"
          onClick={handleSave}
          disabled={!dirty || !!validationError || updateMutation.isPending}
          className="h-9 rounded-[4px] bg-[var(--green)] px-6 text-[11px] font-normal uppercase tracking-[0.1em] text-white hover:bg-[var(--green-hover)] disabled:opacity-40"
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
      {/* Configure Fields dialog (per-segment) */}
      {configSegment && (
        <SegmentFieldConfigDialog
          segment={configSegment}
          open={!!configSegment}
          onOpenChange={(v) => { if (!v) setConfigSegment(null) }}
          config={settings?.data_capture_config}
          onSave={async (cfg) => {
            await updateMutation.mutateAsync({ data_capture_config: cfg })
          }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Early Registration Attributes - per-template configuration
// ---------------------------------------------------------------------------

function EarlyAttributesSection() {
  const { data: templates } = useProductTemplates()
  const [saving, setSaving] = useState<string | null>(null)
  const initialEarly = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const t of templates ?? []) {
      map[t.uuid] = t.early_registration_attributes ?? []
    }
    return map
  }, [templates])
  const [localEarlyDraft, setLocalEarlyDraft] = useState<Record<
    string,
    string[]
  > | null>(null)
  const localEarly = localEarlyDraft ?? initialEarly

  if (!templates?.length) return null

  // Filter to templates that have HOLDING-level attributes
  const templatesWithHolding = templates.filter(
    (t) => t.attributes?.some((a) => a.attribute_level === 'HOLDING'),
  )
  if (!templatesWithHolding.length) return null

  function toggleAttribute(templateUuid: string, codeName: string) {
    setLocalEarlyDraft((prev) => {
      const base = prev ?? initialEarly
      const current = base[templateUuid] ?? []
      const next = current.includes(codeName)
        ? current.filter((c) => c !== codeName)
        : [...current, codeName]
      return { ...base, [templateUuid]: next }
    })
  }

  async function saveTemplate(templateUuid: string) {
    setSaving(templateUuid)
    try {
      await api.put(`/products/templates/${templateUuid}/`, {
        early_registration_attributes: localEarly[templateUuid] ?? [],
      })
      toast.success('Early registration attributes saved')
    } catch (err) {
      toastApiError(err, 'Failed to save early registration attributes');
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="mb-8">
      <p className="clara-section-label mb-4">
        Early Registration Attributes
      </p>
      <p className="clara-small mb-4">
        When using the &quot;Property &amp; Details&quot; step, select which registration
        attributes should be collected before product selection. This enables
        geographic product filtering based on the property address and
        installation date.
      </p>
      {templatesWithHolding.map((tmpl) => {
        const holdingAttrs = (tmpl.attributes ?? []).filter(
          (a) => a.attribute_level === 'HOLDING',
        )
        const early = localEarly[tmpl.uuid] ?? []
        const serverEarly = tmpl.early_registration_attributes ?? []
        const isDirty =
          JSON.stringify([...early].sort()) !== JSON.stringify([...serverEarly].sort())

        return (
          <div
            key={tmpl.uuid}
            className="mb-4 overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]"
          >
            <div className="flex items-center justify-between bg-[var(--bg-subtle)] px-4 py-2.5">
              <span className="text-[12px] text-[var(--ink)]">
                {tmpl.name}
              </span>
              {isDirty && (
                <Button
                  type="button"
                  size="xs"
                  onClick={() => saveTemplate(tmpl.uuid)}
                  disabled={saving === tmpl.uuid}
                  className="rounded-[3px] bg-[var(--green)] px-3 py-1 text-[10px] uppercase tracking-[0.1em] text-white hover:bg-[var(--green-hover)] disabled:opacity-50"
                >
                  {saving === tmpl.uuid ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
            {holdingAttrs.map((attr) => {
              const isEarly = early.includes(attr.code_name)
              return (
                <Button
                  key={attr.code_name}
                  variant="ghost"
                  type="button"
                  onClick={() => toggleAttribute(tmpl.uuid, attr.code_name)}
                  className="flex h-auto w-full items-center justify-start gap-3 rounded-none border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg)] px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-[var(--bg-subtle)]"
                >
                  <Switch checked={isEarly} decorative size="sm" />
                  <div>
                    <span className="block text-[12px] text-[var(--ink)]">
                      {attr.display_name}
                    </span>
                    <span className="block text-[10px] text-[var(--ink-3)]">
                      {attr.code_name} · {attr.field_type.toLowerCase()}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
